/**
 * Affichage d'une position de dépose.
 *
 * POURQUOI LA POSITION PLUTÔT QUE LA PLACE. Les places du parking de Bastia NE
 * SONT PAS NUMÉROTÉES AU SOL. `O09` est un index calculé chez nous : utile pour
 * ne pas empiler deux remorques et pour compter l'occupation, illisible sur le
 * terrain. Envoyer un chauffeur chercher un marquage qui n'existe pas, c'est le
 * faire tourner en rond au milieu de soixante-treize emplacements.
 *
 * Des coordonnées se suivent. C'est la réponse honnête au « où » de la mission,
 * et c'est celle qui doit être au premier plan. Le code de place reste affiché à
 * côté, pour l'exploitant qui raisonne en rangées.
 */

export interface Position {
  latitude: number;
  longitude: number;
  accuracy_m: number | null;
  reliable: boolean;
}

/**
 * Six décimales : environ onze centimètres à cette latitude. En rendre plus
 * afficherait du bruit du récepteur comme s'il s'agissait de précision.
 */
export function formaterCoordonnees(p: Position): string {
  return `${p.latitude.toFixed(6)}, ${p.longitude.toFixed(6)}`;
}

/**
 * Lien de navigation, au schéma `geo:` avant tout autre.
 *
 * `geo:` est le schéma normalisé (RFC 5870) : sur un téléphone, il ouvre
 * l'application de cartographie que l'utilisateur a choisie, pas celle d'un
 * fournisseur qu'on aurait décidé pour lui. Le paramètre `q` porte le libellé,
 * de sorte que l'épingle soit nommée plutôt qu'anonyme.
 */
export function lienCarte(p: Position, libelle?: string): string {
  const base = `geo:${p.latitude},${p.longitude}`;
  const q = `${p.latitude},${p.longitude}`;
  return libelle === undefined
    ? `${base}?q=${q}`
    : `${base}?q=${q}(${encodeURIComponent(libelle)})`;
}

/**
 * La précision annoncée, et l'alerte quand la position n'est pas fiable.
 *
 * Une position marquée non fiable n'est pas une position moins bonne : c'est une
 * position que le moteur a refusé de garantir, faute d'assez de mesures valides
 * pendant l'arrêt. L'afficher comme les autres reviendrait à faire passer une
 * réserve pour une mesure.
 */
export function libellePrecision(p: Position): string {
  const precision =
    p.accuracy_m === null ? '' : `±${p.accuracy_m.toFixed(1).replace('.', ',')} m`;
  if (p.reliable) return precision;
  return precision === '' ? 'non fiable' : `${precision} — non fiable`;
}

/** Construit une position à partir des champs plats rendus par l'administration. */
export function positionDepuisChamps(source: {
  deposit_latitude?: number | null;
  deposit_longitude?: number | null;
  deposit_accuracy_m?: number | null;
  deposit_position_reliable?: boolean | null;
}): Position | null {
  const { deposit_latitude: lat, deposit_longitude: lon } = source;
  // Les deux ensemble ou rien : une latitude seule n'est pas une demi-position,
  // c'est un point faux.
  if (lat === null || lat === undefined || lon === null || lon === undefined) return null;
  return {
    latitude: lat,
    longitude: lon,
    accuracy_m: source.deposit_accuracy_m ?? null,
    reliable: source.deposit_position_reliable ?? false,
  };
}
